
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Song } from '../types';

const AnalyticsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [velocityData, setVelocityData] = useState<any[]>([]);
  const [genreDistribution, setGenreDistribution] = useState<any[]>([]);
  const [liveListeners, setLiveListeners] = useState(0);

  const COLORS = ['#3b82f6', '#8b5cf6', '#64ffda', '#f59e0b', '#ec4899'];

  const fetchAnalytics = async () => {
    const { data: songs } = await supabase.from('songs').select('*').order('created_at', { ascending: true });
    if (songs && songs.length > 0) {
      const dailyMap = new Map();
      songs.forEach((song: Song) => {
        const date = new Date(song.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        dailyMap.set(date, (dailyMap.get(date) || 0) + (song.play_count || 0));
      });
      setVelocityData(Array.from(dailyMap.entries()).map(([time, value]) => ({ time, value })));
      
      const genreMap = new Map();
      songs.forEach((song: Song) => {
        const g = song.genre || 'Other';
        genreMap.set(g, (genreMap.get(g) || 0) + 1);
      });
      setGenreDistribution(Array.from(genreMap.entries()).map(([name, value]) => ({ name, value })));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(() => setLiveListeners(Math.floor(Math.random() * 50) + 200), 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-10 animate-in fade-in duration-700">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-4xl font-black text-white uppercase tracking-tighter">Stats</h2>
          <p className="text-slate-500 font-bold uppercase tracking-[0.3em] text-[10px] mt-1">Real-time performance</p>
        </div>
        <div className="bg-white/5 border border-white/10 px-6 py-3 rounded-2xl flex items-center space-x-3">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
          <span className="text-[10px] font-black text-white uppercase tracking-widest">{liveListeners} Active Now</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 glass p-10 rounded-[2.5rem]">
          <h3 className="text-sm font-black text-slate-500 uppercase tracking-[0.3em] mb-10">Stream Velocity</h3>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={velocityData}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff05" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{fill: '#475569', fontSize: 10, fontWeight: 'bold'}} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#475569', fontSize: 10, fontWeight: 'bold'}} />
                <Tooltip contentStyle={{backgroundColor: '#0f172a', border: 'none', borderRadius: '12px'}} />
                <Area type="monotone" dataKey="value" stroke="#3b82f6" fillOpacity={1} fill="url(#colorValue)" strokeWidth={4} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass p-10 rounded-[2.5rem] flex flex-col">
          <h3 className="text-sm font-black text-slate-500 uppercase tracking-[0.3em] mb-10">Audience Split</h3>
          <div className="flex-1 flex items-center justify-center min-h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={genreDistribution} innerRadius={80} outerRadius={110} paddingAngle={10} dataKey="value">
                  {genreDistribution.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-4 mt-8">
             {genreDistribution.slice(0, 4).map((g, i) => (
               <div key={i} className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full" style={{backgroundColor: COLORS[i % COLORS.length]}}></div>
                    <span className="text-slate-400">{g.name}</span>
                  </div>
                  <span className="text-white">{g.value}</span>
               </div>
             ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
